import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledLink from 'Components/StyledLink'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import {
  type FetchTariffListParams,
  type Tariff,
  TariffStatus
} from 'Services/ProductManagement/Features/Tariff/types'
import useTypedSelector from 'Store'
import {
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC
} from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import useRowSelect from 'Util/hooks/useRowSelect'
import {
  Checkbox,
  type ChipProps,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer
} from '@mui/material'
import Chip from '@mui/material/Chip'
import { t } from 'ttag'

interface TariffListProps {
  rows: Tariff[]
  isLoading: boolean
  params: FetchTariffListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  allowSelect?: boolean
}

function TariffsList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  allowSelect = false
}: Readonly<TariffListProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const { selected, onSelect, onSelectAll } = useRowSelect()
  const { user } = useTypedSelector(state => state.user)
  const { getCodespaceNameByCode } = useAuthorityContext()

  const headers: HeadCell[] = [
    {
      id: 'tariffCode',
      numeric: false,
      disablePadding: false,
      label: t`Tariff code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Tariff description`,
      sortable: true
    },
    ...(IS_WALTTI_SERVICE_ENVIRONMENT && isSuperuser(user)
      ? [
          {
            id: 'pta',
            numeric: false,
            disablePadding: false,
            label: t`Authority`,
            sortable: true
          }
        ]
      : []),
    {
      id: 'valid_from',
      numeric: false,
      disablePadding: false,
      label: t`Starts`,
      sortable: true
    },
    {
      id: 'valid_until',
      numeric: false,
      disablePadding: false,
      label: t`Ends`,
      sortable: false
    },
    {
      id: 'valid_now',
      numeric: false,
      disablePadding: false,
      label: t`State`,
      sortable: false
    },
    {
      id: 'created_timestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: false
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  const chipPropsMapping: Record<TariffStatus, Pick<ChipProps, 'color' | 'label'>> = {
    [TariffStatus.ACTIVE]: { color: 'success', label: t`Active` },
    [TariffStatus.PASSIVE]: { color: 'default', label: t`Passive` },
    [TariffStatus.DRAFT]: { color: 'default', label: t`Draft` },
    [TariffStatus.STAGED]: { color: 'warning', label: t`Production ready` },
    [TariffStatus.ACTIVE_PRE_ORDER]: { color: 'default', label: t`Pre-sale` }
  }

  function getChipPropsByStatus(status: TariffStatus) {
    return chipPropsMapping[status] || { color: 'default', label: t`Unknown` }
  }

  const tariffStatusMapping = {
    [TariffStatus.ACTIVE]: t`Active`,
    [TariffStatus.PASSIVE]: t`Passive`,
    [TariffStatus.DRAFT]: t`Draft`,
    [TariffStatus.STAGED]: t`Production ready`,
    [TariffStatus.ACTIVE_PRE_ORDER]: t`Pre-sale`
  }

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headers}
              onRequestSort={handleRequestSort}
              onSelectAllClick={
                allowSelect
                  ? (_, checked) =>
                      onSelectAll(
                        rows.map(row => row.id),
                        checked
                      )
                  : undefined
              }
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow hover tabIndex={-1} key={`${row.tariff_code}-${row.id}`}>
                    {allowSelect && (
                      <TableCell sx={{ width: 30 }}>
                        <Checkbox
                          size="small"
                          sx={{ p: 0 }}
                          checked={selected.includes(row.id)}
                          onChange={(_, checked) => onSelect(checked, row.id)}
                        />
                      </TableCell>
                    )}
                    <NoWrapTableCell title={showValueOrHyphen(row.tariff_code)}>
                      <StyledLink to={`${baseUrl}/${row.id}/products/`}>
                        {showValueOrHyphen(row.tariff_code)}
                      </StyledLink>
                    </NoWrapTableCell>
                    <NoWrapTableCell title={showValueOrHyphen(row.description)}>
                      <StyledLink to={`${baseUrl}/${row.id}/products/`}>
                        {showValueOrHyphen(row.description)}
                      </StyledLink>
                    </NoWrapTableCell>
                    {IS_WALTTI_SERVICE_ENVIRONMENT && isSuperuser(user) && (
                      <NoWrapTableCell title={getCodespaceNameByCode(row.pta)}>
                        {getCodespaceNameByCode(row.pta)}
                      </NoWrapTableCell>
                    )}
                    <NoWrapTableCell title={datetimeFormatter(row.active_period.valid_from)}>
                      {datetimeFormatter(row.active_period.valid_from)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={datetimeFormatter(row.active_period.valid_until)}>
                      {datetimeFormatter(row.active_period.valid_until)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={tariffStatusMapping[row.status]}>
                      <Chip size="small" {...getChipPropsByStatus(row.status)} />
                    </NoWrapTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}

export default TariffsList
